import React from 'react';
import { IonSelectCustomEvent } from '@ionic/core';
import './Input.scss';
import { IonItem, IonLabel, IonRow, IonSelect, IonSelectOption } from '@ionic/react';
import { DqText } from '../dq-text';
import { GenericMessages } from 'src/intl/GenericMessages';
import { useIntl } from 'react-intl';

export interface InputSelectProps<T> {
  /**
   * The unique identifier of the input
   */
  id?: string;
  /**
   * The name attribute of the input
   */
  name?: string;
  /**
   * The placeholder text for the input
   */
  placeholder?: string;
  /**
   * The label to display above the input
   */
  label?: string;
  /**
   * The error text to display
   */
  errorText?: string;
  /**
   * The value of the select input. Value data type can be passed as a generic type parameter.
   */
  value?: T;
  /**
   * The options to display in the select. Value data type can be passed as a generic type parameter.
   */
  options: { value: T; label: string }[];
  /**
   * Whether or not the input is disabled
   */
  disabled?: boolean;
  /**
   * Whether or not the input is required
   */
  required?: boolean;
  /**
   * The callback function triggered when the input loses focus.
   */
  onChange?: (value: T) => void;
  /**
   * The options to display in the select
   */
  onBlur?: (e: IonSelectCustomEvent<void>) => void;
}

export const InputSelect = <T extends {}>({
  label,
  required = false,
  value,
  placeholder,
  onChange,
  onBlur,
  disabled = false,
  errorText,
  name,
  options,
}: InputSelectProps<T>) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <IonRow className="custom-input-label-row">
        {label && (
          <IonLabel className="custom-input-label" mode="md">
            {label}
          </IonLabel>
        )}
        {required && (
          <div className="custom-input-required-label">
            <span className="red-asterisk">*</span>
            <IonLabel className="label" mode="md">
              {formatMessage(GenericMessages.requiredLabel)}
            </IonLabel>
          </div>
        )}
      </IonRow>
      <IonItem fill="outline" className={!errorText ? 'outlined-input-item' : 'outline-input-item-error'} mode="md">
        <IonSelect
          className="input-select"
          disabled={disabled}
          value={value}
          onIonChange={(event: CustomEvent) => {
            const value = event.detail.value as T;
            onChange && onChange(value);
          }}
          placeholder={placeholder}
          name={name}
          onIonBlur={onBlur}
          interface={'popover'}
          interfaceOptions={{
            size: 'auto',
          }}
        >
          {options?.map((option, index) => (
            <IonSelectOption key={index} value={option.value} className="job-details-update-timezone-item">
              {option.label}
            </IonSelectOption>
          ))}
        </IonSelect>
      </IonItem>
      {errorText && (
        <>
          <span className="red-asterisk"> *</span>
          <DqText>{errorText}</DqText>
        </>
      )}
    </>
  );
};
